/**
 * `failedDevices` slice mutations — Multi-Add Phase 2 W3 (PRD §2.11).
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global): the slice
 * primitive lives in `types.ts::source.failedDevices`; this module owns
 * the pure functional mutations against an immer draft.
 *
 * Per `think-in-systems` (ai.knowledge/global): mutations are exposed as
 * thin helpers that take an immer `Draft<State>` so callers (`runBulkCommit`
 * in wiring.ts, the failed-tile Retry/Remove handlers) compose them via
 * the store's `producer((draft) => …)` boundary. The helpers themselves
 * are framework-agnostic — they can be unit-tested by passing a hand-
 * built draft with no zustand/React in scope.
 *
 * The slice exposes:
 *   - `add(device, reason)` — record a failure with discovery snapshot
 *   - `remove(deviceId)`    — evict (Retry success / Remove click)
 *   - `clear()`             — reset (e.g. new batch start)
 *   - `getAll(state)`       — read-only accessor for renderer subscriptions
 *
 * Scope is per-room because the parent store Provider is itself per-room
 * (the studio re-mounts on room change → state resets to defaults).
 * Slice scope: per-room context, cleared on room-leave (PRD §2.11).
 */
import type { Draft } from './types.js';
import type { FailedDeviceEntry, FailedDeviceReason, State } from './types.js';
import type { Discovery } from '#~/device/discovery/index.js';

/**
 * Record a device as failed inside an immer draft.
 *
 * Idempotent on `deviceId`: a second `add` for the same id replaces the
 * existing entry (newest reason wins). This matches the user mental
 * model for retry — a Retry that itself fails should update the chip
 * copy with the latest reason, not stack a second tile next to the
 * existing one.
 *
 * Per `code-comments-required` (ai.knowledge/global): immer draft is
 * mutated in-place; no return value.
 */
export const addFailedDevice = (
  draft: Draft,
  entry: {
    deviceId: string;
    name: string;
    reason: FailedDeviceReason;
    discovery: Discovery;
  }
): void => {
  const failedAt = Date.now();
  const next: FailedDeviceEntry = {
    deviceId: entry.deviceId,
    name: entry.name,
    reason: entry.reason,
    discovery: entry.discovery,
    failedAt
  };

  // Replace existing entry with the same id (retry-failed scenario);
  // otherwise append. Cast through `readonly[]` because the State type
  // declares the slice as `readonly` but immer's draft is mutable.
  const existing = draft.source.failedDevices as FailedDeviceEntry[];
  const idx = existing.findIndex((e) => e.deviceId === entry.deviceId);
  if (idx >= 0) {
    existing[idx] = next;
  } else {
    existing.push(next);
  }
};

/**
 * Remove the failed-device entry for `deviceId`. Called from the failed
 * tile's Retry success path (state-sync delivered the device — failed
 * tile dissolves) and from the Remove click path (user opts out without
 * re-trying).
 *
 * No-op when the id is not in the slice.
 */
export const removeFailedDevice = (draft: Draft, deviceId: string): void => {
  const existing = draft.source.failedDevices as FailedDeviceEntry[];
  const idx = existing.findIndex((e) => e.deviceId === deviceId);
  if (idx >= 0) {
    existing.splice(idx, 1);
  }
};

/**
 * Clear the entire slice — used when a new bulk batch is started so
 * residue from a prior batch doesn't pollute the failed-tile rendering.
 */
export const clearFailedDevices = (draft: Draft): void => {
  const existing = draft.source.failedDevices as FailedDeviceEntry[];
  existing.length = 0;
};

/**
 * Read-only accessor — returns the current slice contents as a readonly
 * array. Renderer hooks use this through the store selector
 * (`useStore((s) => s.source.failedDevices)`); the helper exists to
 * give a single named import the failed-tile renderer can reuse for
 * tests + reads outside the React tree.
 */
export const getAllFailedDevices = (state: State): readonly FailedDeviceEntry[] =>
  state.source.failedDevices;
